import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiGet, apiPatch, apiPost, apiPostFile, apiPut } from './api';
import {
  IStockOrder,
  IStockOrderData,
  IStockOrderDetail,
  IStockOrderShipmentSerials,
  IStockShipmentUpdateStatus,
  IStockOrderBillingData,
  ICreateStockOrderResponse,
  IUpdateStockOrderBillingDataResponse,
  IStockShipmentTracking,
  IUpdateProductValues,
  IcreateStockOrderComment,
  IStockProductAvailabilityRequest,
  IStockProductAvailabilityData,
  ICreateReservationStockItem,
  IDeleteReservationStockItem
} from '../types/stockOrders';

const { REACT_APP_SUBSCRIPTION } = process.env;

export const createStockOrder = async (body: IStockOrder): Promise<ICreateStockOrderResponse> => {
  return apiPost<AxiosRequestConfig, ICreateStockOrderResponse>(
    `${REACT_APP_SUBSCRIPTION}/stock/order`,
    body as unknown as ICreateStockOrderResponse
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const getStockOrders = async ({
  statusId,
  filters,
  search,
  sort
}: {
  statusId: number;
  filters: string;
  search: string;
  sort: string;
}): Promise<IStockOrderData[]> => {
  const queryParams = [];
  if (filters) queryParams.push(filters);
  if (search) queryParams.push(`search=${search}`);
  if (sort) queryParams.push(sort);

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

  return apiGet<AxiosRequestConfig, IStockOrderData[]>(
    `${REACT_APP_SUBSCRIPTION}/stock/order/trello/${statusId}${queryString}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};
export const getStockOrderFilters = async (): Promise<any> => {
  return apiGet<AxiosRequestConfig, any>(`${REACT_APP_SUBSCRIPTION}/stock/order/trello/filters`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};
export const getStockOrderDetail = async (uuid: string): Promise<IStockOrderDetail> => {
  return apiGet<AxiosRequestConfig, IStockOrderDetail>(`${REACT_APP_SUBSCRIPTION}/stock/order/detail/${uuid}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const updateStockOrderShipmentSerials = async (
  body: IStockOrderShipmentSerials
): Promise<IStockOrderShipmentSerials> => {
  return apiPut<AxiosRequestConfig, IStockOrderShipmentSerials>(`${REACT_APP_SUBSCRIPTION}/product/set-serials`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const updateStockOrderShipmentStatus = async (
  body: IStockShipmentUpdateStatus
): Promise<IStockShipmentUpdateStatus> => {
  return apiPut<AxiosRequestConfig, IStockShipmentUpdateStatus>(
    `${REACT_APP_SUBSCRIPTION}/stock/shipment/set-status/${body?.stockShipmentStatusId}`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const updateStockOrderBillingData = async (
  uuid: string,
  body: Pick<IStockOrderBillingData, 'providerOrderNumber' | 'providerInvoiceNumber'>
): Promise<IUpdateStockOrderBillingDataResponse> => {
  return apiPatch<AxiosRequestConfig, Pick<IStockOrderBillingData, 'providerOrderNumber' | 'providerInvoiceNumber'>>(
    `${REACT_APP_SUBSCRIPTION}/stock/order/billing-data/${uuid}`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const createStockOrderInvoice = async (formData: FormData, uuid: string): Promise<AxiosResponse> => {
  return apiPostFile<AxiosRequestConfig, AxiosResponse>(
    `${REACT_APP_SUBSCRIPTION}/stock/order/invoice/${uuid}`,
    formData
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const cancelStockOrder = async (uuid: string): Promise<void> => {
  return apiPut<AxiosRequestConfig, void>(`${REACT_APP_SUBSCRIPTION}/stock/order/cancel/${uuid}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const updateStockOrderShipmentTracking = async (body: IStockShipmentTracking): Promise<AxiosResponse> => {
  return apiPut<AxiosRequestConfig, IStockShipmentTracking>(`${REACT_APP_SUBSCRIPTION}/stock/shipment/tracking`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const updateProductValues = async (
  stockOrderUuid: string,
  body: IUpdateProductValues[]
): Promise<AxiosResponse> => {
  return apiPatch<AxiosRequestConfig, IUpdateProductValues[]>(
    `${REACT_APP_SUBSCRIPTION}/stock/product/values/${stockOrderUuid}`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const createStockOrderComment = async (body: IcreateStockOrderComment) => {
  const formData = new FormData();
  const bodyJson = JSON.stringify(body);
  formData.append('body', bodyJson);
  body?.commentImage && formData.append('file', body.commentImage);
  return apiPost<AxiosRequestConfig, FormData>(`${REACT_APP_SUBSCRIPTION}/stock-order-comment/backoffice`, formData)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const getStockProductAvailability = async (
  body: IStockProductAvailabilityRequest,
  countryCode: string
): Promise<IStockProductAvailabilityData[]> => {
  return apiPut<AxiosRequestConfig, IStockProductAvailabilityRequest>(
    `${REACT_APP_SUBSCRIPTION}/stock/reservation/availability/${countryCode}`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const createReservationStock = async (body: ICreateReservationStockItem[]): Promise<AxiosResponse> => {
  return apiPost<AxiosRequestConfig, ICreateReservationStockItem[]>(
    `${REACT_APP_SUBSCRIPTION}/stock/reservation/create`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const cancelReservationStock = async (
  body: IDeleteReservationStockItem
): Promise<AxiosResponse> => {
  return apiPut<AxiosRequestConfig, IDeleteReservationStockItem>(
    `${REACT_APP_SUBSCRIPTION}/stock/reservation/cancel`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};
